import type { IBaseClient } from './clients';
import type { ICustomer, IBaseCustomer, IDebitInvoiceReference } from './customers';
import type { PaymentTransactionType, PaymentTransactionProvider } from './payments';

// This is a type which has all the possible fields coming from the BE for an invoice
export type IInvoiceViewRow = {
  id: string;
  invoiceNo: string;

  customerNo: string;
  customerId: string;
  customerName: string;

  clientId: string;
  clientName: string | null;
  clientLogo: string | null;

  countryCode: string;
  dueDate: string;
  invoiceDate: string;

  status: InvoiceStatus;
  stage: InvoiceStage;
  paymentStatus: InvoicePaymentStatus;

  type: 'debit' | 'credit';

  paymentReference: string | null;
  createdAt: string;

  currency: string | null;

  totalRemainingBalance: string;
  totalAmount: string;

  metadata?: {
    allowedStages?: AllowedStages[];
  };

  workflowsSettings?: unknown;

  serviceLineId?: string | null;

  debitInvoiceReferences: IDebitInvoiceReference[];

  payments?: {
    currency: string;
    transactionProvider: PaymentTransactionProvider;
    amount: number;
    createdAt: string;
  }[];

  rank_score?: string | number | null;

  lastPaymentTransactionType?: PaymentTransactionType | null;

  pdfURL?: string | null;

  // New fields
  serviceLineName?: string;
  fee?: number;
  originalTotalAmount?: number;
  paymentMethod?: string;
  paymentDate?: Date;
  lastEscalationDate?: Date;
  whenEscalatingToDebtCollection?: Date;
  paidAmount?: number;
  daysOverdue?: Date;
  orgNo?: string;
  distributionChannel?: EventLogDistributionChannel;
  reminderDate?: Date;
  vatAmount?: number;
  pausedType?: string | null;
  pausedLevel?: string | null;
  pausedDuration?: Date | string | null;
};

// This is what the FE actually expects to see
export type IBaseInvoice = {
  id: string;
  invoiceNo: string;
  customer: IBaseCustomer;
  client: IBaseClient;
  dueDate: string;
  invoiceDate: string;
  status: InvoiceStatus;
  stage: InvoiceStage;
  paymentStatus: InvoicePaymentStatus;
  remainingAmount: number;
  totalRemainingBalance: number;
  countryCode: string;
  paymentReference: string | null;
  currency: string;
  customerNo: string;
  customerId: string;
  metadata?: {
    allowedStages?: AllowedStages[];
  };
  // MISSING
  bankAccount?: {
    currency: string;
    iban: string;
    bankName: string;
    bgBankAccount: string;
    swiftBic: string;
    id: string;
  };
  totalAmount?: number;
  totalCacheAmount?: number;
  ssn?: string;
  debitInvoiceReferences?: IDebitInvoiceReference[];
  score: number;
  lastPaymentTransactionType?: PaymentTransactionType | null;
  pdfURL?: string | null;
  // New fields
  serviceLineName?: string;
  fee?: number;
  originalTotalAmount?: number;
  paymentMethod?: string;
  paymentDate?: Date;
  lastEscalationDate?: Date;
  whenEscalatingToDebtCollection?: Date;
  paidAmount?: number;
  daysOverdue?: Date;
  orgNo?: string;
  distributionChannel?: EventLogDistributionChannel;
  reminderDate?: Date;
  vatAmount?: number;
  pausedType?: string | null;
  pausedLevel?: string | null;
  pausedDuration?: Date | string | null;
};

export type ICompensatoryTransactions = {
  id: string;
  amount: number;
  metadata: {
    transactionType:
      | 'underpayment_within_threshold'
      | 'underpayment_outside_threshold'
      | 'overpayment_within_threshold'
      | 'overpayment_outside_threshold';
    breakdownSummary?: IInvoiceBreakdown;
  };
  transactionType:
    | 'underpayment_within_threshold'
    | 'underpayment_outside_threshold'
    | 'overpayment_within_threshold'
    | 'overpayment_outside_threshold';
  invoiceId: string;
  createdAt: string;
  ownerType: 'serviceLine' | 'customer' | 'invoice' | 'invoiceItem';
};

export enum AllowedStages {
  invoicing = 'invoicing',
  collection = 'collection',
  lawyer = 'lawyer',
}

export type EscalationProgress = {
  /** Step the chain is waiting on or ran last. */
  currentEscalation?: string;
  /** When the current step last fired. Absent while that step is still waiting. */
  lastEscalationDate?: string;
  scheduledPauseCurrentWait?: { step?: string; startedAt?: string };
  /** Pause shifts added to every `dueDate + offset` deadline in that scope. */
  scheduledPauseTimelineShiftMs?: { reminders?: number; dcTransfers?: number };
};

export type ServiceLineMetadata = {
  allowedStages: AllowedStages[];
};

export type ISingleInvoiceResponse = {
  id: string;
  clientId: string;
  serviceLineId: string;
  customerId: string;
  invoiceNo: string;
  pdfURL: string | null;
  digitalInvoiceURL: string | null;
  customerNo: string;
  distributionEmailAddress: string;
  distributionEmailAddress2?: string | null;
  distributionEmailAddress3?: string | null;
  readableClientId: string | null;
  invoiceDate: string;
  dueDate: string;
  countryCode: string;
  city: string;
  zipCode: string;
  addressLine1: string;
  mobilePhoneNo: string;
  paymentReference: string;
  description: string;
  status: InvoiceStatus;
  stage: InvoiceStage;
  paymentStatus: InvoicePaymentStatus;
  feeApplicationDate?: string | null;
  metadata:
    | ({
        invoicing2StopUntilDate?: string | null;
        invoicing4StopUntilDate?: string | null;
        scheduledPauseActivePauses?: Partial<
          Record<string, { scope: 'reminders' | 'dcTransfers' | 'both'; startAt: string }>
        >;
      } & EscalationProgress)
    | null;
  histories: IInvoiceHistory[];
  payments: ITransaction[];
  transactions?: ICompensatoryTransactions[];
  workflowsSettings: Record<string, any>;
  ourReference: string | null;
  yourReference: string | null;
  _deletedIntentionally: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  vatAmount?: string;
  totalRemainingBalance: number;
  remainingAmount: number;
  paidAmount: number;
  totalAmount: number;
  escalationFeeItemIds?: string[];
  client: {
    name: string;
  };
  customer: ICustomer;
  currency?: string;
  serviceLine: {
    name: string;
    id: string;
    bankAccountId: string;
    metadata: {
      allowedStages: AllowedStages[];
    };
    directDebitSettings?: {
      directDebitEnabled?: boolean;
    };
    activeRemindersPause: { startAt: string; endAt: string | null } | null;
    activeDcPause: { startAt: string; endAt: string | null } | null;
  };
  currentBreakdown: IInvoiceBreakdown;
  initialBreakdown: IInvoiceBreakdown;
  invoiceItems: IInvoiceItem[];
  type?: string;
  bankAccount?: {
    currency: string;
    iban: string;
    bankName: string;
    bgBankAccount: string;
    swiftBic: string;
    id: string;
  };
};

export type IInvoiceHistory = {
  id: string;
  invoiceId: string;
  invoiceNo: string;
  amount: number | null;
  type: HistoriesType;
  date: string;
  messageData: {
    from?: string;
    to?: string;
    type?: string;
    pdfUrl?: string;
    dueDate?: string;
    subject?: string;
    distributionChannel?: string;
    remainingAmount?: string;
    ediFallback?: boolean;
    snapshotStatus?: InvoiceStatus;
    newDueDate?: string;
    oldDueDate?: string;
    escalationStopReason?: string;
    paymentTransactionType?: string;
    feeRemoved?: string;
    totalFee?: string;
    previousStage?: string;
    updatedStage?: string;
    willWaiveOutstandingFee?: boolean;
    feeWaiver?: {
      status: 'waived' | 'skipped';
      amount: string;
    };
    metadata?: {
      invoicing2StopUntilDate?: string | null;
      invoicing4StopUntilDate?: string | null;
    };
  };
  currency?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
};

export enum EventLogType {
  DateChange = 'dateChange',
  StatusChange = 'statusChange',
  Payment = 'payment',
  Distribution = 'distribution',
  InvoiceModified = 'invoiceModified',
  Reallocation = 'reallocation',
  StageChange = 'stageChange',
}

export enum EventLogDistributionChannel {
  SMS = 'sms',
  Email = 'email',
  Paper = 'paper',
  EDI = 'edi',
  Einvoice = 'e-invoice',
  Automatic = 'automatic',
  DirectDebit = 'directDebit',
}

export enum EventLogPaymentProvider {
  Bank = 'bank',
  Swish = 'swish',
}

export type InvoiceModifiedEventPayload = {
  modifiedFields: string[];
  oldValues: Record<string, unknown>;
  newValues: Record<string, unknown>;
  transactionCode?: string;
  transactionReference?: string;
  invoiceNo?: string;
  invoiceId?: string;
  customerId?: string;
};

/**
 * Payload of the `dueDateAdjusted` event: what the operator chose to do with the reminder fee, the
 * outstanding reminder fee this adjustment removes, and the outstanding fee left after that.
 * `feeWaiver` arrives a moment after the event itself on a reset, so it is absent on a freshly
 * created entry and on a `keep` decision.
 */
export type DueDateAdjustedEventPayload = {
  feeHandling?: 'reset' | 'keep';
  feeRemoved?: string;
  totalFee?: string;
  feeWaiver?: {
    status: 'waived' | 'skipped';
    amount: string;
  };
};

/** `stageChange` / `invoiceStageReset` — written by the reset-stage confirm TX and completed by the workflow. */
export type PauseStatusChangeEventPayload = {
  origin: 'serviceLine' | 'customer' | 'invoice' | 'none';
  sourceRef: string;
  reason: string | null;
  scope: 'reminders' | 'dcTransfers' | 'both';
  // Present on activation only; `null` means indefinite.
  untilDate?: string | null;
  startAt?: string;
  previousStartAt?: string | null;
  previousEndAt?: string | null;
};

export type StageResetEventPayload = {
  previousStage?: string;
  updatedStage?: string;
  willRedistribute?: boolean;
  willWaiveOutstandingFee?: boolean;
  feeRemoved?: string;
  totalFee?: string;
  feeWaiver?: {
    status: 'waived' | 'skipped';
    amount: string;
  };
};

export interface IEventLog {
  id: string;
  invoiceId: string;
  amount?: number | null;
  type: EventLogType;
  eventDate: string;
  eventName: string;
  triggeredBy?: string | null;
  previousDate?: string | null;
  updatedDate?: string | null;
  statusSnapshot?: InvoiceStatus | null;
  previousStatus?: InvoiceStatus | null;
  updatedStatus?: InvoiceStatus | null;
  updateReason?: string | null;
  distributionChannel?: EventLogDistributionChannel | null;
  distributionProvider?: EventLogPaymentProvider | null;
  distributionSubject?: string | null;
  distributionFrom?: string | null;
  distributionTo?: string | null;
  pdfUrl?: string | null;
  currency?: string | null;
  payload?:
    | InvoiceModifiedEventPayload
    | DueDateAdjustedEventPayload
    | StageResetEventPayload
    | PauseStatusChangeEventPayload
    | null;
  isThereFallback?: boolean;
  isDirectDebitFailed?: boolean;
  paidDate?: string;
}

export type IInvoiceItem = {
  period: {
    value: string;
    inclusive: boolean;
  }[];
  id: string;
  subjectMatter: string;
  name: string;
  unitPrice: string;
  quantity: string;
  VATPercent: string;
  _deletedIntentionally: string | null;
  invoiceId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  totalAmount: string;
  totalAmountNet: string | null;
  VATAmount: string;
  debitItemId: string;
  itemDiscountPercent: string;
  productTag: string;
  itemType: string;
  calcData: {
    totalAmount: number;
    vatAmount: number;
    vatPercent: number;
    totalAmountNet?: number;
  };
};

export type IPaymentAllocation = {
  date: string;
  amount: number;
};

export type IPaymentReversal = {
  date: string;
  amount: number;
  reason: string;
  reversedBy: string;
  invoiceId: string;
};

/** What caused a reminder fee waiver; set by the backend on `metadata.waiverTrigger` (AS-6305). */
export type IReminderFeeWaiverTrigger = {
  source: string;
  mode: 'manual' | 'auto';
  initiatedBy: string;
};

export type ITransaction = {
  id: string;
  paymentAllocationId?: string;
  amount: number;
  currency: string;
  transactionProvider: PaymentTransactionProvider;
  transactionReference: string;
  transactionType: string;
  paymentReference: string;
  paymentBreakdown: IInvoiceBreakdown;
  createdAt: string;
  status: string;
  isReversible: boolean | null;
  allocations: IPaymentAllocation[];
  reversals: IPaymentReversal[];
  paidDate?: string;
  invoicePaymentStatus: InvoicePaymentStatus;
  expectedPaymentStatus: InvoicePaymentStatus;
  metadata?: { waiverTrigger?: IReminderFeeWaiverTrigger } | null;
};

export type IInvoiceStatistics = {
  status: IStatisticsStatus;
  stage: IStatisticsStage;
  paymentStatus: IStatisticsPaymentStatus;
  currency: string;
  dueForDebtCollectionCount: string | number;
};

export type IInvoiceBreakdown = {
  capital: number;
  fee: number;
  serviceFee: number;
  interest: number;
  serviceInterest: number;
};

export type IStatisticsStatus = {
  [InvoiceStatus.Open]: IInvoiceStatisticsRecord;
  [InvoiceStatus.Paused]: IInvoiceStatisticsRecord;
  [InvoiceStatus.Cancelled]: IInvoiceStatisticsRecord;
  [InvoiceStatus.Closed]: IInvoiceStatisticsRecord;
};

export type IStatisticsStage = {
  [InvoiceStage.Invoiced]: IInvoiceStatisticsRecord;
  [InvoiceStage.Reminded]: IInvoiceStatisticsRecord;
  [InvoiceStage.DebtCollection]: IInvoiceStatisticsRecord;
};

export type IStatisticsPaymentStatus = {
  [InvoicePaymentStatus.NotPaid]: IInvoiceStatisticsRecord;
  [InvoicePaymentStatus.Paid]: IInvoiceStatisticsRecord;
  [InvoicePaymentStatus.PartiallyPaid]: IInvoiceStatisticsRecord;
  [InvoicePaymentStatus.CapitalPaid]: IInvoiceStatisticsRecord;
};

export type IInvoiceStatisticsRecord = {
  count: number;
  amount: number;
  originalAmount: number;
  status?: IStatisticsStatus;
};

export enum InvoiceStatus {
  Open = 'open',
  Paused = 'paused',
  Cancelled = 'cancelled',
  Closed = 'closed',
}

export enum InvoiceStage {
  Invoiced = 'invoiced',
  Reminded = 'reminded',
  DebtCollection = 'debtCollection',
}

export const DUE_FOR_DEBT_COLLECTION = 'dueForDebtCollection';

export enum InvoicePaymentStatus {
  NotPaid = 'notPaid',
  PartiallyPaid = 'partiallyPaid',
  Paid = 'paid',
  CapitalPaid = 'capitalPaid',
  // OverPaid = "overPaid",
}

export enum HistoriesType {
  InvoiceDistributed = 'invoiceDistributed',
  SoftReminderDistributed = 'softReminderDistributed',
  DcWarningDistributed = 'dcWarningDistributed',
  DcTransferDistributed = 'dcTransferDistributed',
  ReminderDistributed = 'reminderDistributed',
  PaymentConfirmed = 'paymentConfirmed',
  DueDateUpdated = 'dueDateUpdated',
  DueDateAdjustedFeesKept = 'dueDateAdjustedFeesKept',
  DueDateAdjustedFeesRemoved = 'dueDateAdjustedFeesRemoved',
  GoodWill = 'good_will',
  CreditLoss = 'credit_loss',
  CreditAmount = 'credit_amount',
  DirectPayment = 'direct_payment',
  Cancelled = 'cancelled',
  Bank = 'bank',
  PSP = 'psp',
  DirectDebit = 'direct_debit',
  Automatic = 'automatic',
  StopUntilDateUpdated = 'stopUntilDateUpdated',
  SetupDistributed = 'setupDistributed',
  InvoiceModified = 'invoiceModified',
  PaymentReversal = 'payment_reversal',
  StageReset = 'stageReset',
}

export type ComplexAmount = {
  capital: number;
  fee: number;
};

export type IInvoiceToUpdate = {
  clientId?: string;
  serviceLineName: string;
  invoiceNo?: string;
  transactionType?: string;
  amount?: number | ComplexAmount;
  paidDate?: string;
  additionalInformation?: string;
  confirmFeeAutoRemoval?: boolean;
};

export type ICreditInvoiceRequest = {
  invoiceNo: string;
  distributionEmailAddress: string;
  mobilePhoneNo: string;
  description?: string;
  paymentReference?: string;
  yourReference?: string;
  ourReference?: string;
  distributionMethod: string;
  distributionEnabled: boolean;
  isReverseTaxation: boolean;
  paidDate?: string;
  additionalInformation?: string;
  invoiceItems?: {
    debitItemId?: string;
    name?: string;
    subjectMatter?: string;
    unitPrice: number;
    quantity: number;
    totalAmount?: number;
    calcInput?: {
      totalAmount?: number | null;
      vatAmount?: number | null;
      vatPercent?: number | null;
      totalAmountNet?: number | null;
    };
  }[];
};

export type IInvoiceModifyEligibilityBlockReason = {
  type: string;
  message: string;
  i18nKey?: string;
};

export type IInvoiceModifyEligibility = {
  canReplace: boolean;
  blockReasons: IInvoiceModifyEligibilityBlockReason[];
};

export type IModifyAndResend = {
  distributionMethod: string;
  countryCode?: string;
  city?: string;
  zipCode?: string;
  addressLine1?: string;
  distributionEmailAddress?: string | null;
  distributionEmailAddress2?: string | null;
  distributionEmailAddress3?: string | null;
  mobilePhoneNo?: string;
  ourReference?: string;
  yourReference?: string;
  dueDate?: string;
  distributionMethods?: {
    setup?: string;
    invoicing1?: string;
    invoicing2?: string;
    invoicing3?: string;
    invoicing4?: string;
    credit?: string;
  };
};

export type IInvoiceResetStageModifyInvoice = Omit<
  Partial<IModifyAndResend>,
  'dueDate' | 'distributionMethod'
>;

export type IInvoiceResetStageRequest = {
  targetStage: 'invoiced' | 'reminded';
  // Required when targetStage is 'invoiced'; must be omitted when 'reminded' (server-derived).
  dueDate?: string;
  reason: string;
  redistribute?: boolean;
  modifyInvoice?: IInvoiceResetStageModifyInvoice;
};

export type IInvoiceResetStagePlannedStep = {
  step: string;
  communicateAt: string;
};

export type IInvoiceResetStagePreview = {
  targetStage: 'invoiced' | 'reminded';
  dueDate: string;
  plannedSteps: IInvoiceResetStagePlannedStep[];
  feeWaiveAmount: number;
  feePaidKept: number;
  willWaiveOutstandingFee: boolean;
  willRedistribute: boolean;
  willReRunSetup: false;
};

export enum PaymentClassification {
  UNDERPAYMENT = 'underpayment_threshold',
  OVERPAYMENT = 'overpayment_threshold',
  OVERPAYMENT_OUTSIDE_THRESHOLD = 'overpayment_outside_threshold',
}

// Direct debit retry attempts
export type DirectDebitRetryAttempts = {
  data: {
    timestamp: string;
    errorMessage?: string;
    currentTimestamp?: string;
    retryReason: string;
  }[];
  totalItems: number;
  failedPaymentCount: number;
  successfullAtemptsCount: number;
  directDebitEligible: boolean;
};

type FeeBreakdownItem = {
  amount: number;
  date: string;
  description: string;
  holder: string;
};

type FeeBreakdown = {
  date: string;
  total: number;
  items: FeeBreakdownItem[];
};

export type Breakdown = {
  capital: number;
  fee: number;
  interest: number;
  feeBreakdown?: FeeBreakdown[];
};

interface Transaction {
  date: string;
  description: string;
  holder: string;
  amount: number;
  isSubItem?: boolean;
}

export interface GroupedTransaction {
  date: string;
  total: number;
  items: Transaction[];
}

export type EscalationEnabled = {
  invoicing1: boolean;
  invoicing2: boolean;
  invoicing3: boolean;
  invoicing4: boolean;
  setup: boolean;
  paid: boolean;
  partiallyPaid: boolean;
  credit: boolean;
};

export type DistributionMethods = {
  invoicing1: string;
  invoicing2: string;
  invoicing3: string;
  invoicing4: string;
  setup: string;
  paid: string;
  partiallyPaid: string;
  credit: string;
};
export type EscalationTiming = {
  invoicing1Offset: number;
  invoicing2Offset: number;
  invoicing3Offset: number;
  invoicing4Offset: number;
};

export type InvoiceDocumentPreviewType = 'resend' | 'modifyAndResend';
